/**
 * Generic Catalog Detail Page
 *
 * Mobile-first detail view for catalog items.
 */

import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Pencil, Trash2 } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import api from '@/lib/api'
import { useTranslation } from '@/hooks/useTranslation'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

interface DetailField {
  key: string
  label: string
  type?: 'text' | 'email' | 'phone' | 'address'
  format?: (value: unknown) => string
}

interface CatalogDetailConfig {
  endpoint: string
  basePath: string
  title: string
  editLabel: string
  fields: DetailField[]
  icon: React.ComponentType<{ className?: string }>
}

interface CatalogDetailPageProps {
  config: CatalogDetailConfig
}

export function CatalogDetailPage({ config }: CatalogDetailPageProps) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const Icon = config.icon

  const { data: item, isLoading, error } = useQuery({
    queryKey: [config.endpoint, id],
    queryFn: async () => {
      const response = await api.get(`/catalogs/${config.endpoint}/${id}`)
      return response.data
    },
    enabled: !!id && !isNaN(Number(id)),
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/catalogs/${config.endpoint}/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [config.endpoint] })
      navigate(config.basePath)
    },
  })

  const handleDelete = () => {
    if (confirm(t('common.confirm'))) {
      deleteMutation.mutate()
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (error || !item) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Icon className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <p className="text-lg font-medium mb-2">{t('common.error')}</p>
        <p className="text-sm text-muted-foreground mb-4">
          {t('common.noResults')}
        </p>
        <Button variant="outline" onClick={() => navigate(config.basePath)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back')}
        </Button>
      </div>
    )
  }

  const displayName = item.name || item.description || item.contact_name || item.code || `#${item.id}`

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate(config.basePath)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold truncate">{displayName}</h1>
          <p className="text-sm text-muted-foreground">{config.title}</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2">
        <Button asChild className="flex-1 h-12 md:flex-none">
          <Link to={`${config.basePath}/${id}/edit`}>
            <Pencil className="h-4 w-4 mr-2" />
            {config.editLabel}
          </Link>
        </Button>
        <Button
          variant="outline"
          className="h-12"
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>

      {/* Details Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Icon className="h-4 w-4" />
            {t('common.details')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {config.fields.map((field) => {
            const value = item[field.key]
            if (!value) return null

            return (
              <div key={field.key} className="space-y-1">
                <p className="text-xs text-muted-foreground">{field.label}</p>
                {field.type === 'address' ? (
                  <p className="text-sm whitespace-pre-line">{value}</p>
                ) : field.type === 'email' ? (
                  <a
                    href={`mailto:${value}`}
                    className="text-sm text-primary hover:underline"
                  >
                    {value}
                  </a>
                ) : field.type === 'phone' ? (
                  <a
                    href={`tel:${value}`}
                    className="text-sm text-primary hover:underline"
                  >
                    {value}
                  </a>
                ) : (
                  <p className="text-sm font-medium">
                    {field.format ? field.format(value) : value}
                  </p>
                )}
              </div>
            )
          })}

          {/* Show all fields that have values */}
          {config.fields.every((f) => !item[f.key]) && (
            <p className="text-sm text-muted-foreground text-center py-4">
              {t('common.noResults')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Related Articles (if applicable) */}
      {item.articles_count !== undefined && item.articles_count > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{t('articles.title')}</p>
                <p className="text-sm text-muted-foreground">
                  {item.articles_count} {t('common.records')}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/articles?${config.endpoint.slice(0, -1)}_id=${id}`)}
              >
                {t('common.view')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export type { CatalogDetailConfig, DetailField }
